"use client";

import React, {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {getUserName, getUserRole, checkSession, setDevSession} from "@/features/controllers/AutoTableController";

interface UserContextType {
    userName: string | null;
    userRole: string | null;
    isManager: boolean;
    loading: boolean;
}

const UserContext = createContext<UserContextType>({
    userName: null,
    userRole: null,
    isManager: false,
    loading: true,
});

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUser must be used within UserProvider");
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({children}) => {
    const [userName, setUserName] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    
    const isManager = userRole === "MAN";

    useEffect(() => {
        const initializeAndFetch = async () => {
            try {
                // Najpierw sprawdź czy sesja jest gotowa
                const sessionStatus = await checkSession();
                
                if (!sessionStatus.sessionReady) {
                    // W środowisku deweloperskim automatycznie ustaw sesję deweloperską
                    if (process.env.NODE_ENV === 'development') {
                        // Usuń cudzysłowy i białe znaki z wartości środowiskowych
                        const devUserName = process.env.NEXT_PUBLIC_DEV_USER_NAME?.trim().replace(/^["']|["']$/g, '');
                        const devDefaultFilter = process.env.NEXT_PUBLIC_DEV_DEFAULT_FILTER?.trim().replace(/^["']|["']$/g, '');
                        const devRoot = process.env.NEXT_PUBLIC_DEV_ROOT?.trim().replace(/^["']|["']$/g, '') || 'SN';
                        
                        if (devUserName && devDefaultFilter) {
                            try {
                                console.log(`🔧 Automatyczne ustawianie sesji deweloperskiej: ${devUserName} / ${devDefaultFilter} / ${devRoot}`);
                                await setDevSession(devUserName, devDefaultFilter, devRoot);
                                
                                // Po ustawieniu sesji, sprawdź ponownie i pobierz userName oraz rolę
                                const newSessionStatus = await checkSession();
                                if (newSessionStatus.sessionReady) {
                                    const name = await getUserName();
                                    setUserName(name);
                                    try {
                                        const role = await getUserRole();
                                        setUserRole(role);
                                    } catch (roleError) {
                                        console.warn("⚠️ Nie udało się pobrać roli użytkownika:", roleError);
                                        setUserRole(null);
                                    }
                                    setLoading(false);
                                    return;
                                }
                            } catch (devSessionError: unknown) {
                                const errorMessage = devSessionError instanceof Error ? devSessionError.message : "Nieznany błąd";
                                console.warn("⚠️ Nie udało się ustawić sesji deweloperskiej:", errorMessage);
                            }
                        } else {
                            console.warn("⚠️ Brak zmiennych środowiskowych dla sesji deweloperskiej");
                        }
                    } else {
                        console.warn("Sesja nie jest zainicjowana:", sessionStatus.message);
                    }
                    
                    setUserName(null);
                    setLoading(false);
                    return;
                }

                const name = await getUserName();
                setUserName(name);
                
                // Pobierz rolę użytkownika
                try {
                    const role = await getUserRole();
                    setUserRole(role);
                } catch (roleError) {
                    console.warn("⚠️ Nie udało się pobrać roli użytkownika:", roleError);
                    setUserRole(null);
                }
            } catch (error: unknown) {
                console.error("Błąd podczas sprawdzania sesji lub pobierania userName:", error);
                
                // Jeśli błąd wskazuje na brak logowania, pokaż odpowiedni komunikat
                const errorMessage = error instanceof Error ? error.message : String(error);
                if (errorMessage.includes('401') || errorMessage.includes('requiresLogin')) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn("⚠️ Aby aplikacja działała, zaloguj się w głównym projekcie PHP (http://localhost/grupabemo)");
                    }
                }
                
                setUserName(null);
            } finally {
                setLoading(false);
            }
        };

        initializeAndFetch();
    }, []);

    return (
        <UserContext.Provider value={{userName, userRole, isManager, loading}}>
            {children}
        </UserContext.Provider>
    );
};

